import { ReactElement, SyntheticEvent, useEffect, useRef, useState, useContext } from "react"
import { InputGroup, RadioButton, Search, Button, Modal, Loading } from "@wfp/ui"
import { List, AutoSizer, WindowScroller, ListRowProps } from "react-virtualized"
import {
  faCalculator,
  faFingerprint,
  faListUl,
  faLongArrowAltLeft,
  faLongArrowAltRight,
  faRotateLeft,
  faTimesCircle,
  faTrash,
} from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { useClickAway, useDebounce, useLockBodyScroll, useWindowScroll } from "react-use"

import Select, { SingleValue } from "react-select"

import { useUpdatePersonalInfoMutation, useUpdateXlsFormMappingMutation } from "services/household/xlsFormSchemas"
import useCommon from "hooks/useCommon"

import OverflowText from "components/Shared/OverflowText/OverflowText"
import CustomOption from "components/Shared/Common/CustomOption"
import XlsFormPopover from "components/Household/XslForm/XlsFormPopover"
import XslFormCalculated from "components/Household/XslForm/XslFormCalculated"

import { FormField, MappingField, OptionGroup, PayloadMappingField } from "interfaces/mappingInterfaces"

import { TypeFilter, XslMappingContext } from "providers/XslMappingContext"
import { HH_MODULE } from "constants/constants"
import { checkAccess } from "utils/helpers"
import { useAppSelector } from "hooks/hooksStore"
import { selectUser } from "features/common/commonSlice"
import XlsFormChoice from "./XlsFormChoice"

type Props = {
  formFields?: FormField[]
  xlsFormId: number
}

const initialMappingField: MappingField = {
  mappingId: 0,
  baseFieldId: 0,
  baseFieldType: 0,
  baseFieldName: "",
  baseFieldLabel: "",
  fieldLabel: "",
  groupName: "",
  fieldName: "",
  formula: "",
  formulaFields: [],
}

const XslFormMapping = ({ formFields, xlsFormId }: Props): ReactElement => {
  const { showSnackBar } = useCommon()
  const {
    isLoaded,
    fieldsList,
    mappedField,
    setFilterType,
    filterType,
    setCurrentGroupFilter,
    handleSearch,
    handleDiscardField,
    currentGroupFilter,
    totalFields,
    handleAddCalculatedField,
    handleRemoveCalculatedField,
    handleAddFormulaField,
    optionsGroup,
    inputSearch,
    setInputSearch,
  } = useContext(XslMappingContext)

  const popOverReference = useRef<HTMLDivElement>(null)
  const listReference = useRef<List>(null)
  const user = useAppSelector(selectUser)

  const [updateMapping, { isLoading: isUpdatingMapping }] = useUpdateXlsFormMappingMutation()
  const [updatePersonalInfo] = useUpdatePersonalInfoMutation()

  const [showPopover, setShowPopover] = useState<"map" | "calculated" | null>(null)
  const [inputCoSearch, setInputCoSearch] = useState<string>("")
  const [choiceField, setChoiceField] = useState<string>("")
  const [currentScroll, setCurrentScroll] = useState<number>(0)

  const [calculateIndex, setCalculateIndex] = useState<number>(-1)

  const [currentMappingField, setCurrentMappingField] = useState<MappingField>(initialMappingField)

  useClickAway(popOverReference, e => {
    // @ts-ignore
    e.target?.tagName !== "svg" && setShowPopover(false)
  })
  useLockBodyScroll(!!showPopover)
  const { y } = useWindowScroll()

  const onFilterChange = (e: SyntheticEvent<HTMLInputElement>): void => {
    setFilterType(e.currentTarget.value as TypeFilter)
  }

  const removeField = async (field: MappingField): Promise<void> => {
    try {
      const payload = { ...field, xlsFormId }
      await updateMapping(payload).unwrap()
      showSnackBar("Field unmapped successfully")
    } catch (error) {
      showSnackBar(`Field error ${error}`)
    }
  }

  const handleMapping = async (field: FormField): Promise<void> => {
    const payload = { ...currentMappingField, fieldName: field.fieldName, fieldId: field.fieldId }

    try {
      await updateMapping({ ...payload, xlsFormId }).unwrap()
      showSnackBar("Field mapped successfully")
    } catch (error) {
      showSnackBar(`Field ${!field.fieldId ? "unmapped" : "mapped"} failed`, "error")
    }
    setCurrentMappingField(initialMappingField)
  }

  const onPopOverAction = (field: FormField): void => {
    if (showPopover === "map") {
      handleMapping(field)
    } else {
      handleAddCalculatedField(field, calculateIndex)
      setCalculateIndex(-1)
      listReference?.current?.forceUpdateGrid()
      listReference?.current?.recomputeRowHeights()
    }
    setShowPopover(null)
  }

  const onRemoveFormulaField = (index: number, calculatedIndex: number): void => {
    const field = fieldsList[index]
    if (field.formulaFields?.length === 1) {
      removeField({ ...field, formulaFields: [], formula: "", fieldName: "" })
    }
    handleRemoveCalculatedField(field, index, calculatedIndex)
  }

  const onAddFormula = (value: string, index: number): void => {
    const field = fieldsList[index]
    handleAddFormulaField(field, value, index)
  }

  const onConfirmAction = async (index: number): Promise<void> => {
    const formulaFields = fieldsList[index].formulaFields?.map(field => ({
      xlsFormFieldId: field.fieldId,
      xlsFormFieldName: field.fieldName,
    }))
    const field: PayloadMappingField = { ...fieldsList[index], formulaFields }
    try {
      await updateMapping({ ...field, xlsFormId }).unwrap()
      showSnackBar("Field mapped successfully")
    } catch (error) {
      showSnackBar("Field mapped failed", "error")
    }
  }

  const mappedInPercent = (): number => {
    const mapped = Object.keys(mappedField).length
    return Math.round((mapped / totalFields) * 100)
  }

  const handleUpdatePersonalInfo = async (field: MappingField): Promise<void> => {
    if (!checkAccess(user?.roles, [HH_MODULE], "xlsForms", "pii")) return
    try {
      await updatePersonalInfo({
        xlsFormFieldId: field.baseFieldId,
        isPersonalInfo: !field.isPersonalInfo,
      }).unwrap()
      showSnackBar("Personal info updated successfully")
    } catch (error) {
      showSnackBar("Personal info update failed", "error")
    }
  }

  useDebounce(
    () => {
      handleSearch(inputSearch, inputCoSearch)
    },
    300,
    [inputSearch, currentGroupFilter, filterType, inputCoSearch],
  )

  useEffect(() => {
    listReference?.current?.forceUpdateGrid()
    listReference?.current?.recomputeRowHeights()
  }, [fieldsList])

  useEffect(() => {
    if (isLoaded) {
      setTimeout(() => {
        window.scrollTo(0, currentScroll)
        setCurrentScroll(0)
      }, 10)
    } else {
      setCurrentScroll(y)
    }
  }, [isLoaded])

  const RenderMappedField = ({ index }: { index: number }): ReactElement => {
    return (
      <>
        <div className="border border-sky-600 border-solid p-1 px-2 me-4 flex items-center min-w-[200px] xl:min-w-[450px]">
          <FontAwesomeIcon icon={faLongArrowAltLeft} className="me-2" color="var(--support-02)" />
          <div className="font-bold text-sm">{fieldsList[index].fieldName}</div>
          <div className="ms-2 max-w-[300px] text-xs table table-fixed w-full">
            <div className="table-cell">
              <OverflowText dark>{fieldsList[index].fieldLabel}</OverflowText>
            </div>
          </div>
        </div>
        <FontAwesomeIcon
          icon={faTimesCircle}
          className="ms-1 cursor-pointer"
          color="var(--support-01)"
          size="lg"
          onClick={() =>
            removeField({
              ...initialMappingField,
              mappingId: fieldsList[index].mappingId,
              baseFieldId: fieldsList[index].baseFieldId,
              baseFieldName: fieldsList[index].baseFieldName,
            })
          }
        />
        {fieldsList[index]?.baseListId ? (
          <FontAwesomeIcon
            icon={faListUl}
            className="ms-2 cursor-pointer"
            color={fieldsList[index]?.listIsMapped ? "var(--support-02)" : "var(--interactive-01)"}
            size="lg"
            onClick={() => setChoiceField(fieldsList[index].fieldName)}
          />
        ) : null}
      </>
    )
  }

  const RenderMapActions = ({ index }: { index: number }): ReactElement => {
    return (
      <>
        <Button
          className="border border-sky-600 border-solid p-1 px-2 bg-white text-black font-normal hover:bg-blue-200"
          onClick={() => {
            if (isUpdatingMapping) return
            setCurrentMappingField({
              ...initialMappingField,
              mappingId: fieldsList[index].mappingId,
              baseFieldId: fieldsList[index].baseFieldId,
              baseFieldName: fieldsList[index].baseFieldName,
              baseFieldType: fieldsList[index].baseFieldType,
            })
            setShowPopover("map")
          }}
        >
          <FontAwesomeIcon icon={faLongArrowAltRight} className="me-2" color="var(--interactive-01)" />
          Map to a CO field
        </Button>
        <Button
          className="ml-1.5 border border-sky-600 border-solid p-1 px-2 bg-white text-black font-normal hover:!bg-white"
          onClick={() => {
            setCalculateIndex(index)
            setShowPopover("calculated")
          }}
        >
          <FontAwesomeIcon icon={faCalculator} className="me-2" color="var(--interactive-01)" />
          Calculate
        </Button>
      </>
    )
  }

  const rowRenderer = ({ key, index, style, field }): ReactElement => {
    return (
      <div
        key={`${key}-${field.baseFieldName}`}
        style={{ ...style }}
        className="flex w-full border-solid border-b border-gray-200 align-center"
      >
        {fieldsList && (
          <>
            <div
              className="w-6/12 grid grid-cols-3 gap-2 grid-flow-col"
              style={{
                gridTemplateColumns: "3fr",
              }}
            >
              <div className="col-span-2">
                <div className="font-bold text-sm">{field.baseFieldName}</div>
                <OverflowText dark>{field.baseFieldLabel}</OverflowText>
              </div>
              <div className="flex items-center justify-between">
                <FontAwesomeIcon
                  icon={faFingerprint}
                  className={`px-2 ${checkAccess(user?.roles, [HH_MODULE], "xlsForms", "pii") ? "cursor-pointer" : ""}`}
                  color={field.isPersonalInfo ? "var(--interactive-01)" : "#ccc"}
                  onClick={() => handleUpdatePersonalInfo(field)}
                />

                {!field?.fieldName && !field.formula ? (
                  <FontAwesomeIcon
                    icon={filterType === TypeFilter.DISCARDED ? faRotateLeft : faTrash}
                    className="pe-2 cursor-pointer"
                    color={filterType === TypeFilter.DISCARDED ? "var(--interactive-01)" : "var(--support-01)"}
                    onClick={() => {
                      if (isUpdatingMapping) return
                      handleDiscardField(field.mappingId)
                    }}
                  />
                ) : null}
              </div>
            </div>
            {filterType !== TypeFilter.DISCARDED && (
              <div className="w-6/12 ps-4 border-l-2 border-gray-200 border-solid flex text-sm h-full align-center justify-end">
                {field?.fieldName || field.formulaFields?.length ? (
                  field?.formulaFields?.length ? (
                    <XslFormCalculated
                      field={field as MappingField}
                      onRemoveAction={(calculatedIndex: number) => {
                        onRemoveFormulaField(index, calculatedIndex)
                      }}
                      onAddAction={(): void => {
                        setCalculateIndex(index)
                        setShowPopover("calculated")
                      }}
                      onConfirmAction={() => onConfirmAction(index)}
                      onAddFormula={value => onAddFormula(value, index)}
                    />
                  ) : (
                    <RenderMappedField index={index} />
                  )
                ) : (
                  <RenderMapActions index={index} />
                )}
              </div>
            )}
          </>
        )}
      </div>
    )
  }

  return (
    <>
      <div className="flex w-full">
        <div className="flex flex-col w-6/12 pe-4">
          <h1 className="text-2xl">Base HQ Form Fields</h1>

          <div className="text-sm flex flex-row align-center">
            <span>
              Form coverage: {Object.keys(mappedField).length} fields out of {totalFields}
            </span>
            <span className="ml-2">
              <div className="h-2 w-20 bg-neutral-200 rounded-md">
                <div className="h-2 bg-blue-400 rounded-md" style={{ width: `${mappedInPercent()}%` }} />
              </div>
            </span>
          </div>

          <div className="mt-2 flex align-center justify-between w-100">
            <Select
              className="w-4/6 select-wrapper"
              classNamePrefix="select"
              placeholder="Select group"
              options={optionsGroup || []}
              getOptionLabel={option => option.label}
              getOptionValue={option => option.value}
              isSearchable
              components={{ Option: CustomOption }}
              value={currentGroupFilter}
              onChange={value => setCurrentGroupFilter(value as SingleValue<OptionGroup>)}
              isClearable
            />
            <div className="w-2/6 ml-4">
              <Search
                id="search-xls-form"
                onChange={value => setInputSearch(value as unknown as string)}
                value={inputSearch}
              />
            </div>
          </div>

          <div className="mapping-radio-button mt-1">
            <InputGroup>
              <RadioButton
                value={TypeFilter.ALL}
                labelText="Show all"
                checked={filterType === TypeFilter.ALL}
                onChange={onFilterChange}
              />
              <RadioButton
                value={TypeFilter.UNMAPPED}
                labelText="Show unmapped"
                checked={filterType === TypeFilter.UNMAPPED}
                onChange={onFilterChange}
              />
              <RadioButton
                value={TypeFilter.MAPPED}
                labelText="Show mapped"
                checked={filterType === TypeFilter.MAPPED}
                onChange={onFilterChange}
              />
              <RadioButton
                value={TypeFilter.DISCARDED}
                labelText="Show discarded"
                checked={filterType === TypeFilter.DISCARDED}
                onChange={onFilterChange}
              />
            </InputGroup>
          </div>
        </div>

        <div className="flex flex-col w-6/12 ps-4">
          <h1 className="text-2xl">CO Form Fields</h1>

          <div className="text-sm flex flex-row align-center">
            <span>
              Mapped fields: {Object.keys(mappedField).length} fields out of {formFields?.length || 0}
            </span>
            <span className="ml-2">
              <div className="h-2 w-20 bg-neutral-200 rounded-md">
                <div
                  className="h-2 bg-blue-400 rounded-md"
                  style={{ width: `${(Object.keys(mappedField).length / (formFields?.length || 1)) * 100}%` }}
                />
              </div>
            </span>
          </div>

          <div className="mt-2 flex justify-end">
            <span className="w-6/12">
              <Search
                id="search-xls-co-form"
                onChange={value => {
                  setInputCoSearch(value as unknown as string)
                }}
                value={inputCoSearch}
              />
            </span>
          </div>

          {showPopover && (
            <XlsFormPopover
              fieldList={formFields}
              fieldName={currentMappingField?.baseFieldName || ""}
              ref={popOverReference}
              closePopover={() => {
                setCalculateIndex(-1)
                setShowPopover(null)
              }}
              onAction={onPopOverAction}
              mappedFields={mappedField}
            />
          )}
        </div>
      </div>

      {isUpdatingMapping && <Loading small />}
      {isLoaded && (
        <div className="mt-4">
          <WindowScroller scrollElement={window}>
            {({ height, scrollTop }) => (
              <AutoSizer disableHeight size={`size-${fieldsList.length}`}>
                {({ width }) => (
                  <List
                    ref={listReference}
                    autoHeight
                    width={width}
                    height={height}
                    rowCount={fieldsList?.length || 0}
                    rowHeight={({ index }) => {
                      const rowField = fieldsList[index]
                      if (rowField?.formulaFields?.length) {
                        return 45 * Number(rowField.formulaFields?.length) + 45
                      }
                      return 45
                    }}
                    scrollTop={scrollTop - 320}
                    rowRenderer={({ key, index, style }: ListRowProps) =>
                      rowRenderer({
                        key,
                        index,
                        style,
                        field: fieldsList[index],
                      })
                    }
                  />
                )}
              </AutoSizer>
            )}
          </WindowScroller>
        </div>
      )}

      <Modal open={!!choiceField} onRequestClose={() => setChoiceField("")} wide passiveModal lazyLoad>
        <div className="flex flex-col w-full min-h-[500px]">
          <XlsFormChoice formFields={formFields} question={choiceField} />
        </div>
      </Modal>
    </>
  )
}

export default XslFormMapping
